import { Link, type LinkProps } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LeadSection } from "@/components/LeadSection";
import { LeadMagnet } from "@/components/LeadMagnet";
import { StickyCta } from "@/components/StickyCta";
import { InlineCta } from "@/components/InlineCta";


export type ArticleSection = { h: string; p: string[]; list?: string[] };

/** Einheitliches Layout für alle Ratgeber-Artikel inkl. Lead-Blöcken. */
export function ArticlePage({
  kicker,
  title,
  intro,
  sections,
  faqs,
  source,
  related,
  answer,
  breadcrumb = { to: "/ratgeber", label: "Ratgeber" },
  updated,
}: {
  kicker: string;
  title: string;
  intro: string;
  sections: ArticleSection[];
  faqs: { q: string; a: string }[];
  source: string;
  related: { to: LinkProps["to"]; label: string }[];
  /** Kurzantwort in zwei bis drei Sätzen, direkt zitierbar. */
  answer?: string;
  breadcrumb?: { to: LinkProps["to"]; label: string };
  /** ISO-Datum der letzten inhaltlichen Aktualisierung. */
  updated?: string;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <article>
          <header className="veil border-b border-border/60 py-16 md:py-20">
            <div className="mx-auto max-w-3xl px-5">
              <nav aria-label="Brotkrumen" className="mb-5 text-xs text-muted-foreground">
                <Link to="/" className="hover:text-foreground">Start</Link>
                <span className="px-2">/</span>
                <Link to={breadcrumb.to} className="hover:text-foreground">
                  {breadcrumb.label}
                </Link>
              </nav>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {kicker}
              </p>
              <h1 className="mt-5 text-3xl font-bold leading-[1.12] md:text-5xl">{title}</h1>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{intro}</p>
              <a
                href="#anfrage"
                className="shadow-signal mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
              </a>
              {updated && (
                <p className="mt-4 text-xs text-muted-foreground">
                  Zuletzt aktualisiert:{" "}
                  <time dateTime={updated}>
                    {new Date(updated).toLocaleDateString("de-DE", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </time>
                </p>
              )}
            </div>
          </header>

          <div className="mx-auto max-w-3xl px-5 py-14">
            {answer && (
              <section className="mb-12 rounded-lg border border-primary/40 bg-primary/5 p-5 md:p-6">
                <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">
                  Kurze Antwort
                </h2>
                <p data-answer className="mt-3 text-base leading-relaxed text-foreground">
                  {answer}
                </p>
              </section>
            )}

            <section className="mb-12 rounded-lg border border-border bg-card p-5 md:p-6">
              <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">
                Auf einen Blick
              </h2>
              <ul className="mt-4 grid gap-3">
                {sections.slice(0, 5).map((s) => (
                  <li key={s.h} className="text-sm leading-relaxed text-muted-foreground">
                    <span className="font-semibold text-foreground">{s.h}: </span>
                    {(s.p[0] ?? "").split(". ")[0]}.
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-12 grid gap-4 sm:grid-cols-3" aria-label="Ihr Weg zur Lösung">
              {[
                { n: "01", h: "Problem einordnen", p: `Sie erkennen, was bei „${title}“ wirklich wichtig ist.` },
                { n: "02", h: "Nächsten Schritt wählen", p: "Sie sehen, was Sie selbst lösen können und wo Hilfe sinnvoll ist." },
                { n: "03", h: "Sauber umsetzen", p: "Wenn Sie Unterstützung möchten, erhalten Sie vorab Aufwand und Kosten." },
              ].map((item) => (
                <div key={item.n} className="border-l border-primary/50 pl-4">
                  <span className="font-mono text-[11px] text-primary">{item.n}</span>
                  <h2 className="mt-1 text-base font-semibold">{item.h}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.p}</p>
                </div>
              ))}
            </section>

            {sections.map((s, i) => (
              <div key={s.h}>
                <section className="mb-11">
                  <h2 className="text-2xl font-bold md:text-3xl">{s.h}</h2>
                  {s.p.map((t) => (
                    <p key={t} className="mt-4 leading-relaxed text-muted-foreground">
                      {t}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="mt-5 grid gap-2.5">
                      {s.list.map((l) => (
                        <li
                          key={l}
                          className="flex items-start gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm"
                        >
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          {l}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                {i % 2 === 1 && i < sections.length - 1 && (
                  <InlineCta
                    text={`Möchten Sie wissen, welcher nächste Schritt bei „${title}“ für Ihren Shop sinnvoll ist?`}
                    cta="Kostenlose Einschätzung erhalten"
                  />
                )}
              </div>
            ))}


            <div className="my-12">
              <LeadMagnet source={`${source}-checkliste`} />
            </div>

            <section>
              <h2 className="text-2xl font-bold md:text-3xl">Häufige Fragen</h2>
              <div className="mt-6 divide-y divide-border overflow-hidden rounded-lg border border-border">
                {faqs.map((f) => (
                  <div key={f.q} className="bg-card p-5">
                    <h3 className="text-base font-semibold">{f.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <nav className="mt-12 flex flex-wrap gap-4 text-sm font-semibold text-primary">
              {related.map((r) => (
                <Link key={String(r.to)} to={r.to} className="inline-flex items-center gap-1.5">
                  {r.label} <ArrowUpRight className="h-4 w-4" />
                </Link>
              ))}
            </nav>
          </div>
        </article>

        <LeadSection source={source} />
      </main>
      <StickyCta />
      <SiteFooter />
    </div>
  );
}
